import {
  Flex,
  type FlexProps,
  NestedContentItem,
  ReportSection,
  ReportSectionItemContent,
} from '@/components';
import type { LedgerKsOwnershipRatioSectionProps } from '@/features/mold/models/ledger';
import {
  DiscriminatedInspectionEvaluationModel,
  ExistenceOrAbsenceLabelModifier,
} from '@/features/project/projects/_models/project';
import type { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LEDGER_BORDER,
  formatOwnershipRatioFraction,
  formatOwnershipRatioPartsOfFraction,
} from '../../../utils';

const LABEL_WIDTH = 198;

export const KsOwnershipRatioSection: FC<
  LedgerKsOwnershipRatioSectionProps
> = ({ project }) => {
  const { t } = useTranslation();

  const parsed = DiscriminatedInspectionEvaluationModel.safeParse(project);

  if (!parsed.success || parsed.data.sheetType !== 'KS') {
    return null;
  }

  const { inspectionEvaluation } = parsed.data;

  return (
    <ReportSection
      title={t('ownership-ratio')}
      items={[
        {
          subtitle: t('ownership-ratio'),
          item: (
            <Flex vertical>
              <Flex>
                <OwnershipRatioContentItem
                  isFirst
                  value={formatOwnershipRatioFraction(
                    inspectionEvaluation.ownershipRatio?.ratioNumerator,
                    inspectionEvaluation.ownershipRatio?.ratioDenominator,
                  )}
                  wrapperStyle={{
                    flex: 'none',
                    width: LABEL_WIDTH,
                    minWidth: LABEL_WIDTH,
                  }}
                />
                <OwnershipRatioContentItem
                  label={t('ownership-ratio-numerator')}
                  value={formatOwnershipRatioPartsOfFraction(
                    inspectionEvaluation.ownershipRatio?.ratioNumerator,
                  )}
                  wrapperStyle={{ height: '100%' }}
                />
                <OwnershipRatioContentItem
                  label={t('ownership-ratio-denominator')}
                  value={formatOwnershipRatioPartsOfFraction(
                    inspectionEvaluation.ownershipRatio?.ratioDenominator,
                  )}
                  wrapperStyle={{
                    height: '100%',
                  }}
                />
              </Flex>
              <OwnershipRatioContentItem
                isFirst
                label={t('contents-of-held-fund-amount-of-ownership-ratio')}
                value={
                  inspectionEvaluation.ownershipRatio?.basis
                    ?.contentsOfHeldFundAmount
                }
              />
              <NestedContentItem
                mainRowData={[
                  {
                    label: t(
                      'contents-of-fund-project-costs-of-ownership-ratio',
                    ),
                    value:
                      inspectionEvaluation.ownershipRatio?.basis
                        ?.contentsOfFundProjectCosts,
                  },
                ]}
                subRowsData={[
                  {
                    label: t('concept-of-contents-of-each-term'),
                    value:
                      inspectionEvaluation.ownershipRatio
                        ?.conceptOfProjectProspect?.contents,
                  },
                  {
                    label: t(
                      'basis-for-estimating-indicators-for-project-prospect',
                    ),
                    value:
                      inspectionEvaluation.ownershipRatio
                        ?.conceptOfProjectProspect
                        ?.basisForCalculationOfIndicators,
                  },
                  {
                    label: t(
                      'indicators-for-project-prospect-and-recent-performance',
                    ),
                    value:
                      inspectionEvaluation.ownershipRatio
                        ?.conceptOfProjectProspect?.moreRecentResults,
                  },
                ]}
                labelWidth={LABEL_WIDTH}
                isLast
              />
            </Flex>
          ),
        },
        {
          subtitle: t('low-used-fund-investigation'),
          item: (
            <Flex vertical>
              <Flex>
                <OwnershipRatioContentItem
                  isFirst
                  label={t('has-ceased-operations')}
                  value={
                    typeof inspectionEvaluation.lowUsedFundInvestigation
                      ?.hasCeasedOperations === 'boolean'
                      ? inspectionEvaluation.lowUsedFundInvestigation
                          .hasCeasedOperations
                        ? ExistenceOrAbsenceLabelModifier.true
                        : ExistenceOrAbsenceLabelModifier.false
                      : null
                  }
                  labelWidth={150}
                />
                <OwnershipRatioContentItem
                  label={t('has-no-operation-fund-detected')}
                  value={
                    typeof inspectionEvaluation.lowUsedFundInvestigation
                      ?.hasNoResultRecent === 'boolean'
                      ? inspectionEvaluation.lowUsedFundInvestigation
                          .hasNoResultRecent
                        ? ExistenceOrAbsenceLabelModifier.true
                        : ExistenceOrAbsenceLabelModifier.false
                      : null
                  }
                  labelWidth={150}
                />
                <OwnershipRatioContentItem
                  label={t('has-fund-that-lost-purpose')}
                  value={
                    typeof inspectionEvaluation.lowUsedFundInvestigation
                      ?.hasFundThatLostPurpose === 'boolean'
                      ? inspectionEvaluation.lowUsedFundInvestigation
                          .hasFundThatLostPurpose
                        ? ExistenceOrAbsenceLabelModifier.true
                        : ExistenceOrAbsenceLabelModifier.false
                      : null
                  }
                  labelWidth={150}
                />
                <OwnershipRatioContentItem
                  label={t('has-exceed-1-significantly')}
                  value={
                    typeof inspectionEvaluation.lowUsedFundInvestigation
                      ?.hasExceedOneSignificantly === 'boolean'
                      ? inspectionEvaluation.lowUsedFundInvestigation
                          .hasExceedOneSignificantly
                        ? ExistenceOrAbsenceLabelModifier.true
                        : ExistenceOrAbsenceLabelModifier.false
                      : null
                  }
                  labelWidth={150}
                />
                <OwnershipRatioContentItem
                  label={t('has-deemed-unlikely-to-be-used')}
                  value={
                    typeof inspectionEvaluation.lowUsedFundInvestigation
                      ?.hasDeemedUnlikelyToBeUsed === 'boolean'
                      ? inspectionEvaluation.lowUsedFundInvestigation
                          .hasDeemedUnlikelyToBeUsed
                        ? ExistenceOrAbsenceLabelModifier.true
                        : ExistenceOrAbsenceLabelModifier.false
                      : null
                  }
                  labelWidth={150}
                />
              </Flex>
              <OwnershipRatioContentItem
                isFirst
                label={t(
                  'reason-for-selecting-absense-when-ownership-ratio-exceeds-1',
                )}
                value={
                  inspectionEvaluation.lowUsedFundInvestigation
                    ?.reasonsForBeingFalseWhileExceedingOne
                }
              />
              <OwnershipRatioContentItem
                isFirst
                label={t(
                  'consideration-where-the-fund-are-unlikely-to-be-used',
                )}
                value={
                  inspectionEvaluation.lowUsedFundInvestigation
                    ?.resultsOfInvestigations
                }
              />
              <OwnershipRatioContentItem
                isFirst
                label={t(
                  'reason-for-leaving-the-fund-that-are-unlikely-to-be-used',
                )}
                value={
                  inspectionEvaluation.lowUsedFundInvestigation
                    ?.reasonsForLeaving
                }
                isLast
              />
            </Flex>
          ),
        },
      ]}
    />
  );
};

const OwnershipRatioContentItem: FC<{
  label?: string;
  isSpaceLabel?: boolean;
  value: ReactNode;
  labelWidth?: number;
  wrapperStyle?: FlexProps['style'];
  isLast?: boolean;
  isFirst?: boolean;
}> = ({
  label,
  isSpaceLabel,
  value,
  labelWidth = LABEL_WIDTH,
  isLast,
  isFirst,
  wrapperStyle,
}) => {
  const inspectionEvaluationLabelStyle = {
    width: labelWidth,
    minWidth: labelWidth,
  };
  return (
    <ReportSectionItemContent
      label={label}
      isSpaceLabel={isSpaceLabel}
      value={value}
      labelStyle={inspectionEvaluationLabelStyle}
      wrapperStyle={{
        borderLeft: isFirst ? undefined : LEDGER_BORDER,
        borderBottom: isLast ? undefined : LEDGER_BORDER,
        height: 'auto',
        ...wrapperStyle,
      }}
    />
  );
};
